import axios, { AxiosError } from 'axios';
import refreshApi from '@/auth/signin/agent/RefreshTokenApiAgent';
import { toast } from '@/common/view/Toast';

export const DOMAIN = process.env.REACT_APP_SERVER_URL;
export const API_V1 = '/api/v1';

// &create_department_ids[]=1&create_department_ids[]=2같이 호출되어 다음 함수로 &create_department_ids=1&create_department_ids=2로 바꾼다.
const serializeParams = (params: Record<string, unknown>): string => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) usp.append(key, String(v));
      });
    } else if (value !== undefined && value !== null) {
      usp.append(key, String(value));
    }
  });
  return usp.toString();
};

const ApiAgent = axios.create({
  baseURL: `${DOMAIN}${API_V1}`,
  paramsSerializer: {
    serialize: serializeParams,
  },
});


export default ApiAgent;

export const addAccessTokenRequestInterceptor = (accessToken: string): number => {
  return ApiAgent.interceptors.request.use(
    (config) => {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
      return config;
    },
    (error) => Promise.reject(error),
  );
};

export type OnAccessTokenRefreshed = (
  accessToken: string,
  expiresAt: string,
  failedUrl?: string,
) => Promise<void>;
export const addUnauthorizedResponseInterceptor = (
  onAccessTokenRefreshed: OnAccessTokenRefreshed,
  onRefreshTokenExpired: () => Promise<void>,
): number => {
  return ApiAgent.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error?.response?.status === 401) {
        try {
          const { data } = await refreshApi();
          error.config!.headers['Authorization'] = `Bearer ${data.access_token}`;
          await onAccessTokenRefreshed(
            data.access_token,
            data.access_token_expires_at,
            error.config?.url,
          );
        } catch (refreshError) {
          await onRefreshTokenExpired();
          return Promise.reject(error);
        }

        return axios(error.config!);
      } else {
        return Promise.reject(error);
      }
    },
  );
};

export const getCustomError = <T>(error: unknown): T | undefined => {
  return (error as AxiosError<T>).response?.data;
};

type CustomErrorData = {
  timestamp: string;
  path: string;
  detail: {
    code: string;
    message: string;
  };
};
export const handleError = (unknownError: unknown) => {
  if (axios.isCancel(unknownError)) {
    // Request was intentionally canceled
  } else {
    const error = unknownError as AxiosError<CustomErrorData>;
    if (error.response?.data) {
      const data: CustomErrorData = error.response.data;
      toast.error(data.detail.message);
    } else if (error.message === 'Network Error') {
      toast.error('서버와 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } else if (error.message) {
      toast.error(error.message);
    }
  }
};

export const getErrorPayload = (error: unknown): CustomErrorData | null => {
  if (axios.isCancel(error)) {
    return null;
  }

  const axiosError = error as AxiosError<CustomErrorData>;
  if (!axiosError.response) {
    return null;
  }
  if (!axiosError.response.data) {
    return null;
  }
  return axiosError.response.data;
};
